import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import {
  streamingServiceApi,
  EstreamingServiceStatus,
  type StreamingService,
  type PricePlan,
} from "@/services/streaming-service";
import {
  type StreamingAccount,
  ESlotStatus,
  EStreamingAccountStatus,
  streamingAccountService,
} from "@/services/streaming-account";
import { StatusBadge } from "@/components/dashboard/StatusBadge";
import { StatCard } from "@/components/dashboard/StatCard";
import { RegisterStreamingAccountModal } from "@/components/streaming-accounts/RegisterStreamingAccountModal";
import { PricePlanModal } from "@/components/streaming-services/PricePlanModal";
import { EditStreamingServiceModal } from "@/components/streaming-services/EditStreamingServiceModal";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Plus,
  Edit,
  DollarSign,
  Eye,
  EyeOff,
  Ban,
  Trash2,
  Server,
  Users,
  CircleDollarSign,
  LayoutGrid,
  Loader2,
  Truck,
  ExternalLink,
} from "lucide-react";
import { cn, formatCurrency } from "@/lib/utils";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { ESubscriptionStatus, EownerType, subscriptionService } from "@/services/subscription";

interface AccountsTabProps {
  serviceId: string;
  accounts: StreamingAccount[];
  isLoading: boolean;
  onAddAccount: () => void;
}

function AccountsTab({
  serviceId,
  accounts,
  isLoading,
  onAddAccount,
}: AccountsTabProps) {
  const [showInactive, setShowInactive] = useState(false);

  const activeAccounts = accounts.filter(
    (account) => account.status !== EStreamingAccountStatus.INACTIVE,
  );
  const inactiveAccountsCount = accounts.length - activeAccounts.length;
  const visibleAccounts = showInactive ? accounts : activeAccounts;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-foreground">
          Accounts ({activeAccounts.length})
        </h3>
        <div className="flex items-center gap-2">
          {inactiveAccountsCount > 0 && (
            <Button
              size="sm"
              variant="outline"
              className="gap-2"
              onClick={() => setShowInactive((prev) => !prev)}
            >
              {showInactive ? (
                <EyeOff className="h-4 w-4" />
              ) : (
                <Eye className="h-4 w-4" />
              )}
              {showInactive
                ? "Ocultar desactivadas"
                : `Ver desactivadas (${inactiveAccountsCount})`}
            </Button>
          )}
          <Button size="sm" className="gap-2" onClick={onAddAccount}>
            <Plus className="h-4 w-4" />
            Add Account
          </Button>
        </div>
      </div>

      <div className="card-elevated overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center">
            <Loader2 className="h-6 w-6 animate-spin mx-auto text-primary" />
            <p className="text-muted-foreground mt-2">Cargando cuentas...</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Account Email</TableHead>
                <TableHead className="text-center">Total Slots</TableHead>
                <TableHead className="text-center">Used</TableHead>
                <TableHead className="text-center">Available</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visibleAccounts.map((account) => {
                const usedSlots = account.slots.filter(
                  (s) => s.status === ESlotStatus.OCCUPIED
                ).length;
                const availableSlots = account.totalSlots - usedSlots;
                const isFullyOccupied = availableSlots === 0;

                return (
                  <TableRow
                    key={account.id}
                    className={cn(isFullyOccupied && "bg-warning/5")}
                  >
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        {isFullyOccupied && (
                          <span className="w-2 h-2 rounded-full bg-warning" />
                        )}
                        {account.email}
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      {account.totalSlots}
                    </TableCell>
                    <TableCell className="text-center">
                      <span className="text-destructive font-medium">
                        {usedSlots}
                      </span>
                    </TableCell>
                    <TableCell className="text-center">
                      <span className="text-success font-medium">
                        {availableSlots}
                      </span>
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={account.status} />
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              asChild
                            >
                              <Link to={`/accounts/${account.id}`}>
                                <Eye className="h-4 w-4" />
                              </Link>
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>View account</TooltipContent>
                        </Tooltip>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                            >
                              <Ban className="h-4 w-4 text-destructive" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Disable account</TooltipContent>
                        </Tooltip>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Edit account</TooltipContent>
                        </Tooltip>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}

        {!isLoading && visibleAccounts.length === 0 && (
          <div className="p-8 text-center">
            <p className="text-muted-foreground">
              {accounts.length === 0
                ? "No accounts for this service yet"
                : "No active accounts for this service"}
            </p>
            <Button variant="link" className="mt-2" onClick={onAddAccount}>
              Add your first account
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

interface PricingTabProps {
  service: StreamingService;
  onAddPlan: () => void;
  onEditPlan: (plan: PricePlan) => void;
  onDeletePlan: (plan: PricePlan) => void;
  isDeletingPlan: boolean;
}

interface PricePlanTableProps {
  plans: PricePlan[];
  showSupplierColumn?: boolean;
  onEditPlan: (plan: PricePlan) => void;
  onDeletePlan: (plan: PricePlan) => void;
  isDeletingPlan: boolean;
  onAddPlan: () => void;
  emptyLabel: string;
}

function PricePlanTable({
  plans,
  showSupplierColumn,
  onEditPlan,
  onDeletePlan,
  isDeletingPlan,
  onAddPlan,
  emptyLabel,
}: PricePlanTableProps) {
  const navigate = useNavigate();

  return (
    <div className="card-elevated overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Nombre</TableHead>
            {showSupplierColumn && <TableHead>Proveedor</TableHead>}
            <TableHead>Duración</TableHead>
            <TableHead>Tipo</TableHead>
            <TableHead>Precio</TableHead>
            <TableHead>Estado</TableHead>
            <TableHead className="text-right">Acciones</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {plans.map((plan) => (
            <TableRow key={plan.id}>
              <TableCell className="font-medium">
                <div className="flex items-center gap-2">
                  {plan.name}
                  {plan.isDefault && (
                    <span className="px-2 py-0.5 text-xs rounded-full bg-primary/10 text-primary font-medium">
                      Default
                    </span>
                  )}
                </div>
              </TableCell>
              {showSupplierColumn && (
                <TableCell>
                  {plan.supplierId ? (
                    <button
                      type="button"
                      className="flex items-center gap-1 text-primary hover:underline focus:outline-none group"
                      onClick={() => navigate(`/suppliers/${plan.supplierId}`)}
                    >
                      <span>{plan.supplier.name}</span>
                      <ExternalLink className="h-3 w-3 opacity-0 group-hover:opacity-60 transition-opacity" />
                    </button>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </TableCell>
              )}
              <TableCell className="text-muted-foreground">{plan.duration} días</TableCell>
              <TableCell className="text-muted-foreground">{plan.pricePlanType}</TableCell>
              <TableCell className="font-semibold">${formatCurrency(plan.price)}</TableCell>
              <TableCell>
                <StatusBadge status={plan.status} />
              </TableCell>
              <TableCell className="text-right">
                <div className="flex items-center justify-end gap-1">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => onEditPlan(plan)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Edit plan</TooltipContent>
                  </Tooltip>
                  <AlertDialog>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <AlertDialogTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            disabled={isDeletingPlan}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </AlertDialogTrigger>
                      </TooltipTrigger>
                      <TooltipContent>Delete plan</TooltipContent>
                    </Tooltip>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>¿Eliminar plan de precio?</AlertDialogTitle>
                        <AlertDialogDescription>
                          El plan <strong>{plan.name}</strong> será eliminado
                          permanentemente. Esta acción no se puede deshacer.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancelar</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => onDeletePlan(plan)}
                          disabled={isDeletingPlan}
                          className="bg-destructive hover:bg-destructive/90 text-destructive-foreground"
                        >
                          Eliminar
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {plans.length === 0 && (
        <div className="p-8 text-center">
          <p className="text-muted-foreground">{emptyLabel}</p>
          <Button variant="link" className="mt-2" onClick={onAddPlan}>
            Add your first price plan
          </Button>
        </div>
      )}
    </div>
  );
}

function PricingTab({
  service,
  onAddPlan,
  onEditPlan,
  onDeletePlan,
  isDeletingPlan,
}: PricingTabProps) {
  const [showInactive, setShowInactive] = useState(false);

  const servicePricePlans = service.pricePlans || [];
  const clientPricePlans = servicePricePlans.filter(
    (plan) => plan.ownerType !== EownerType.SUPPLIER,
  );
  const supplierPricePlans = servicePricePlans.filter(
    (plan) => plan.ownerType === EownerType.SUPPLIER,
  );

  const activeClientPlans = clientPricePlans.filter(
    (plan) => plan.status !== EstreamingServiceStatus.INACTIVE,
  );
  const activeSupplierPlans = supplierPricePlans.filter(
    (plan) => plan.status !== EstreamingServiceStatus.INACTIVE,
  );
  const inactivePricePlansCount =
    servicePricePlans.length - activeClientPlans.length - activeSupplierPlans.length;

  const visibleClientPlans = showInactive ? clientPricePlans : activeClientPlans;
  const visibleSupplierPlans = showInactive ? supplierPricePlans : activeSupplierPlans;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-foreground">
          Price Plans ({activeClientPlans.length + activeSupplierPlans.length})
        </h3>
        <div className="flex items-center gap-2">
          {inactivePricePlansCount > 0 && (
            <Button
              size="sm"
              variant="outline"
              className="gap-2"
              onClick={() => setShowInactive((prev) => !prev)}
            >
              {showInactive ? (
                <EyeOff className="h-4 w-4" />
              ) : (
                <Eye className="h-4 w-4" />
              )}
              {showInactive
                ? "Ocultar desactivados"
                : `Ver desactivados (${inactivePricePlansCount})`}
            </Button>
          )}
          <Button size="sm" className="gap-2" onClick={onAddPlan}>
            <Plus className="h-4 w-4" />
            Add Price Plan
          </Button>
        </div>
      </div>

      <div className="space-y-3">
        <h4 className="text-sm font-semibold text-muted-foreground flex items-center gap-2">
          <Users className="h-4 w-4" />
          Planes de Clientes ({activeClientPlans.length})
        </h4>
        <PricePlanTable
          plans={visibleClientPlans}
          onEditPlan={onEditPlan}
          onDeletePlan={onDeletePlan}
          isDeletingPlan={isDeletingPlan}
          onAddPlan={onAddPlan}
          emptyLabel="No hay planes de precio para clientes"
        />
      </div>

      <div className="space-y-3">
        <h4 className="text-sm font-semibold text-muted-foreground flex items-center gap-2">
          <Truck className="h-4 w-4" />
          Planes de Proveedor ({activeSupplierPlans.length})
        </h4>
        <PricePlanTable
          plans={visibleSupplierPlans}
          showSupplierColumn
          onEditPlan={onEditPlan}
          onDeletePlan={onDeletePlan}
          isDeletingPlan={isDeletingPlan}
          onAddPlan={onAddPlan}
          emptyLabel="No hay planes de precio de proveedor"
        />
      </div>
    </div>
  );
}

export default function ServiceDetail() {
  const { serviceId } = useParams<{ serviceId: string }>();
  const queryClient = useQueryClient();

  // Estados para modales
  const [isAccountModalOpen, setIsAccountModalOpen] = useState(false);
  const [isPricePlanModalOpen, setIsPricePlanModalOpen] = useState(false);
  const [isEditServiceModalOpen, setIsEditServiceModalOpen] = useState(false);
  const [editingPricePlan, setEditingPricePlan] = useState<PricePlan | null>(
    null
  );

  // Obtener el servicio desde el API
  const {
    data: serviceResponse,
    isLoading: isLoadingService,
    error: serviceError,
  } = useQuery({
    queryKey: ["streaming-service", serviceId],
    queryFn: () => streamingServiceApi.getById(parseInt(serviceId)),
    enabled: !!serviceId,
  });

  const service = serviceResponse?.data;

  // Eliminar plan de precio
  const deletePricePlanMutation = useMutation({
    mutationFn: (pricePlanId: number) =>
      streamingServiceApi.deletePricePlan(pricePlanId),
    onSuccess: () => {
      toast.success("Plan de precio eliminado exitosamente");
      queryClient.invalidateQueries({ queryKey: ["streaming-service", serviceId] });
      queryClient.invalidateQueries({ queryKey: ["streaming-services"] });
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  // Obtener las cuentas de streaming asociadas a este servicio
  const {
    data: accountsResponse,
    isLoading: isLoadingAccounts,
  } = useQuery({
    queryKey: ["streaming-accounts", "by-service", serviceId],
    queryFn: () =>
      streamingAccountService.getAll({
        serviceId: parseInt(serviceId),
        limit: 100,
      }),
    enabled: !!serviceId,
  });

  const serviceAccounts = accountsResponse?.data || [];

  // Obtener las suscripciones activas de este servicio (para el ingreso mensual)
  const { data: subscriptionsResponse } = useQuery({
    queryKey: [
      "subscriptions",
      "by-service",
      serviceId,
      ESubscriptionStatus.ACTIVE,
    ],
    queryFn: () =>
      subscriptionService.getAll({
        serviceId: parseInt(serviceId),
        status: ESubscriptionStatus.ACTIVE,
        limit: 100,
      }),
    enabled: !!serviceId,
  });

  const activeSubscriptions = subscriptionsResponse?.data || [];

  const [isActive, setIsActive] = useState(false);

  // Actualizar isActive cuando se cargue el servicio
  useEffect(() => {
    if (service) {
      setIsActive(service.status === "ACTIVE");
    }
  }, [service]);

  // Calcular estadisticas de las cuentas
  const totalSlots = serviceAccounts.reduce(
    (sum, a) => sum + a.totalSlots,
    0
  );
  const occupiedSlots = serviceAccounts.reduce(
    (sum, a) =>
      sum + a.slots.filter((s) => s.status === ESlotStatus.OCCUPIED).length,
    0
  );
  const monthlyRevenue = activeSubscriptions.reduce(
    (sum, sub) => sum + (Number(sub.price) || 0),
    0
  );

  // Handlers para modales
  const handleAddAccount = () => {
    setIsAccountModalOpen(true);
  };

  const handleAddPricePlan = () => {
    setEditingPricePlan(null);
    setIsPricePlanModalOpen(true);
  };

  const handleEditPricePlan = (plan: PricePlan) => {
    setEditingPricePlan(plan);
    setIsPricePlanModalOpen(true);
  };

  const handleDeletePricePlan = (plan: PricePlan) => {
    deletePricePlanMutation.mutate(plan.id);
  };

  // Loading state
  if (isLoadingService) {
    return (
      <DashboardLayout title="Loading..." subtitle="">
        <div className="card-elevated p-8 text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
          <p className="text-muted-foreground mt-2">Cargando servicio...</p>
        </div>
      </DashboardLayout>
    );
  }

  // Error state
  if (serviceError) {
    return (
      <DashboardLayout title="Error" subtitle="">
        <Alert variant="destructive">
          <AlertDescription>
            Error al cargar el servicio. Por favor, intenta de nuevo.
          </AlertDescription>
        </Alert>
        <div className="mt-4">
          <Button variant="link" asChild>
            <Link to="/services">Back to Services</Link>
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  // Not found state
  if (!service) {
    return (
      <DashboardLayout title="Service Not Found" subtitle="">
        <div className="card-elevated p-8 text-center">
          <p className="text-muted-foreground">
            The requested service could not be found.
          </p>
          <Button variant="link" asChild className="mt-2">
            <Link to="/services">Back to Services</Link>
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      title=""
      subtitle=""
      customHeader={
        <div className="space-y-4">
          {/* Breadcrumb */}
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/services">Services</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>{service.name}</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>

          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div
                className="w-14 h-14 rounded-xl flex items-center justify-center text-3xl"
                style={{ backgroundColor: `${service.color}15` }}
              >
                {service.icon}
              </div>
              <div>
                <div className="flex items-center gap-3">
                  <h1 className="text-2xl font-bold text-foreground">
                    {service.name}
                  </h1>
                  <div className="flex items-center gap-2">
                    <Switch checked={isActive} onCheckedChange={setIsActive} />
                    <span className="text-sm text-muted-foreground">
                      {isActive ? "Active" : "Inactive"}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                className="gap-2"
                onClick={handleAddAccount}
              >
                <Plus className="h-4 w-4" />
                Add Account
              </Button>
              <Button
                variant="outline"
                className="gap-2"
                onClick={handleAddPricePlan}
              >
                <DollarSign className="h-4 w-4" />
                Manage Pricing
              </Button>
              <Button
                variant="outline"
                className="gap-2"
                onClick={() => setIsEditServiceModalOpen(true)}
              >
                <Edit className="h-4 w-4" />
                Edit Service
              </Button>
            </div>
          </div>
        </div>
      }
    >
      <div className="space-y-6 animate-fade-in">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            title="Total Accounts"
            value={serviceAccounts.length}
            icon={Server}
          />
          <StatCard title="Total Slots" value={totalSlots} icon={LayoutGrid} />
          <StatCard title="Occupied Slots" value={occupiedSlots} icon={Users} />
          <StatCard
            title="Monthly Revenue"
            value={`$${formatCurrency(monthlyRevenue)}`}
            icon={CircleDollarSign}
            trend={{ value: 12, isPositive: true }}
          />
        </div>

        {/* Tabs */}
        <Tabs defaultValue="accounts" className="space-y-4">
          <TabsList>
            <TabsTrigger value="accounts">Accounts</TabsTrigger>
            <TabsTrigger value="pricing">Pricing</TabsTrigger>
          </TabsList>

          <TabsContent value="accounts">
            <AccountsTab
              serviceId={serviceId || ""}
              accounts={serviceAccounts}
              isLoading={isLoadingAccounts}
              onAddAccount={handleAddAccount}
            />
          </TabsContent>

          <TabsContent value="pricing">
            <PricingTab
              service={service}
              onAddPlan={handleAddPricePlan}
              onEditPlan={handleEditPricePlan}
              onDeletePlan={handleDeletePricePlan}
              isDeletingPlan={deletePricePlanMutation.isPending}
            />
          </TabsContent>
        </Tabs>
      </div>

      {/* Modales */}
      <RegisterStreamingAccountModal
        open={isAccountModalOpen}
        onOpenChange={setIsAccountModalOpen}
        defaultServiceId={serviceId ? parseInt(serviceId) : undefined}
      />

      <PricePlanModal
        open={isPricePlanModalOpen}
        onOpenChange={setIsPricePlanModalOpen}
        service={service}
        editPlan={editingPricePlan}
      />

      <EditStreamingServiceModal
        open={isEditServiceModalOpen}
        onOpenChange={setIsEditServiceModalOpen}
        service={service}
      />
    </DashboardLayout>
  );
}
